<template>
  <v-card class="dialog-card add-condition">
    <ModalHeader
      icon="mdi-motion-play-outline"
      modalTitle="Add Condition"
      @close="emit('close')" />
    <div class="modal-body">
      <div style="overflow-y: auto; height: 500px; width: 100%">
        <transition name="slide-fade">
          <div
            v-if="selected_conditions.length > 0"
            class="multi-selection-bar">
            <div class="selection-count">
              {{ selected_conditions.length }} conditions selected
            </div>
            <v-spacer />
            <v-btn
              variant="text"
              color="error"
              @click="clearSelection"
              class="mx-1">
              <v-icon>mdi-close</v-icon>
              Clear
            </v-btn>
          </div>
        </transition>
        <div
          v-for="(play_condition, i) in deluge.all_conditions"
          :key="(play_condition, i)"
          @click="handleConditionClick(play_condition, i)">
          <WideCard
            v-if="
              !play_condition?.hidden &&
              !play_condition?.deprecated &&
              !inRejectCategory(play_condition)
            "
            :loading="false"
            :icon="play_condition.icon"
            :title="play_condition.name"
            :selected="isSelected(i)"
            :dense="true"
            class="condition-card">
            <div
              class="pl-1 condition-description"
              :class="{ 'selected-text': isSelected(i) }"
              style="opacity: 0.8; font-size: clamp(10px, 2vw, 14px)">
              {{ play_condition.description }}
            </div>
          </WideCard>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <v-spacer />
      <v-btn
        @click="AddConditions()"
        variant="flat"
        block
        color="primary"
        :disabled="selected_index === -1 && selected_conditions.length === 0">
        Add
        {{
          selected_conditions.length > 0
            ? selected_conditions.length + ' '
            : ''
        }}Condition{{ selected_conditions.length !== 1 ? 's' : '' }}
      </v-btn>
    </div>
  </v-card>
</template>
<script setup>
import { onMounted, ref, defineProps, onBeforeMount, watch } from 'vue';
import WideCard from '../Shared/WideCard.vue';
import { $http } from '../../stores/http.js';
import { deluge, state } from '../../stores/deluge.js';
import ModalHeader from './Modals/ModalHeader.vue';

const emit = defineEmits(['newcondition', 'close']);
const props = defineProps(['item', 'itemtype', 'play_conditions', 'did']);
const selected_index = ref(-1);
const selected_condition = ref(undefined);
const selected_conditions = ref([]);
const ctrl_down = ref(false);

// Watch for ctrl key events
watch(
  () => state.ctrl_down,
  (newVal) => {
    ctrl_down.value = newVal;
  }
);

function inRejectCategory(play_condition) {
  // Handle asset and asset_variant itemtype explicitly
  // Both should check for 'asset_variant' in rejects array
  if (props.itemtype === 'asset' || props.itemtype === 'asset_variant') {
    return play_condition.rejects?.includes('asset_variant') || false;
  }

  const itemType = props.item.item_type === 3 ? 'scene' : 'queue';

  // Determine if this is a parent_queue context
  // For default conditions, check explicit flags
  // For live items, check if parent exists in deluge.items
  let isParentQueue = '';
  if (props.item.is_standalone_queue) {
    // Standalone queue - has a channel parent, so it IS a parent_queue context
    isParentQueue = 'parent_queue';
  } else if (props.item.is_nested_queue) {
    // Nested queue - has a queue parent, so it's NOT a parent_queue context
    isParentQueue = '';
  } else if (props.item.pdid) {
    // Live item - check if parent exists in deluge
    isParentQueue =
      deluge.items[props.item.pdid]?.item_type === undefined
        ? 'parent_queue'
        : '';
  }

  return (
    play_condition.rejects?.includes(itemType) ||
    play_condition.rejects?.includes(isParentQueue) ||
    false
  );
}

onMounted(() => {
  selected_index.value = -1;
});

function handleConditionClick(condition, index) {
  if (!ctrl_down.value) {
    // Regular click - select only one item
    selected_index.value = index;
    selected_condition.value = condition;
    selected_conditions.value = [];
  } else {
    // Ctrl click - toggle selection
    const conditionIndex = selected_conditions.value.findIndex(
      (c) => c.index === index
    );

    if (conditionIndex === -1) {
      // Add to selection
      selected_conditions.value.push({
        condition,
        index,
      });
    } else {
      // Remove from selection
      selected_conditions.value.splice(conditionIndex, 1);
    }

    // Clear the single selection
    selected_index.value = -1;
    selected_condition.value = undefined;
  }
}

function isSelected(index) {
  if (selected_index.value === index) {
    return true;
  }
  return selected_conditions.value.some((item) => item.index === index);
}

function clearSelection() {
  selected_conditions.value = [];
  selected_index.value = -1;
  selected_condition.value = undefined;
}

function AddConditions() {
  props.item.conditions_edited = true;

  if (selected_conditions.value.length > 0) {
    // Add multiple conditions
    const newConditions = selected_conditions.value.map((item) => {
      const condition = JSON.parse(JSON.stringify(item.condition));

      // For asset variants, only store essential fields + UID
      if (props.itemtype === 'asset_variant') {
        const essentialCondition = {
          script: condition.script,
          uid: crypto.randomUUID(),
          data: condition.data,
        };
        return essentialCondition;
      }

      // For live items, use full condition metadata (no UID needed)
      return condition;
    });

    // For asset variants, directly push to array to maintain reference
    if (props.itemtype === 'asset_variant') {
      props.item.conditions.play_conditions.push(...newConditions);
    } else {
      // For live items, create new array reference for reactivity
      props.item.conditions.play_conditions = [
        ...props.item.conditions.play_conditions,
        ...newConditions,
      ];
    }
  } else if (selected_condition.value) {
    // Add single condition (legacy behavior)
    const conditionCopy = JSON.parse(JSON.stringify(selected_condition.value));

    // Initialize conditions structure if needed
    if (!props.item.conditions) {
      props.item.conditions = { play_conditions: [], end_conditions: [] };
    }
    if (!Array.isArray(props.item.conditions.play_conditions)) {
      props.item.conditions.play_conditions = [];
    }

    // For asset variants, only store essential fields + UID
    if (props.itemtype === 'asset_variant' || props.itemtype === 'asset') {
      const essentialCondition = {
        script: conditionCopy.script,
        uid: crypto.randomUUID(),
        data: conditionCopy.data,
      };
      props.item.conditions.play_conditions.unshift(essentialCondition);
    } else {
      // For live items, use full condition metadata and create new array reference
      props.item.conditions.play_conditions = [
        conditionCopy,
        ...props.item.conditions.play_conditions,
      ];
    }
  }

  emit('close');
}
</script>
<style scoped>
.add-condition .modal-body {
  padding: 20px;
  padding-top: 0px;
  padding-bottom: 0px;
}

.multi-selection-bar {
  display: flex;
  align-items: center;
  background-color: var(--v-cardColor-base);
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.selection-count {
  font-weight: 500;
  color: var(--text);
}

.selected-text {
  color: white !important;
}

/* Hover effect for condition description */
.condition-card:hover .condition-description {
  color: white !important;
}

/* Animation for multi-selection bar */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
